import type {
  AccessibilitySection,
  BedInputWire,
  ContentLocale,
  CustomFieldInput,
  FeatureCardInput,
  ImportantInformationSection,
  LocationFeatureInput,
  MediaListResponse,
  MediaUploadResult,
  NearbyPlaceInput,
  PoliciesSection,
  PropertyTabCounts,
  PropertyType,
  RoomInputWire,
  SafetySection,
  ScenicViewInput,
  TaxonomyGroup,
  UpdateAboutBody,
  UpdateAccessibilityBody,
  UpdateBasicsBody,
  UpdateEstateBody,
  UpdateImportantInformationBody,
  UpdateLocationBody,
  UpdatePoliciesBody,
  UpdateRatingBody,
  UpdateSafetyBody,
  UpdateSupplierBody,
  VideoUploadSession,
  VideoUploadSessionStatus,
  VideoUploadComplete,
  WizardVersionPayload,
} from "@/types";
import { ACCEPTED_PHOTO_TYPES, PHOTO_MAX_BYTES, ROUTES } from "@/lib/constants";
import { AccessRestrictedError, throwIfNotOk, throwFromBody } from "@/lib/api/http-error";
import { invalidateWizardHydration } from "@/lib/wizard-hydration-cache";
import { mergeVariantsToWire, type FieldVariant } from "@/lib/utils";
import { ensureActiveLanguagesLoaded, getActiveLanguageCodes } from "@/stores/locale-store";
import type { PropertyListTab } from "@/stores/catalogue-store";

/** A row's local id before it's ever been saved (e.g. `Math.random().toString(36)`, `optimistic-*`) is never a real UUID — only forward `id` to the API when it actually is one, or a brand-new row gets rejected as an update to a row that doesn't exist. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A NestJS validation 400 — `message` is an array of per-field messages
 * (e.g. "countryCode must be longer than or equal to 2 characters").
 * Thrown instead of a plain Error where a caller highlights individual
 * fields rather than showing one combined string.
 */
export class ApiValidationError extends Error {
  fieldMessages: string[];
  constructor(fieldMessages: string[]) {
    super(fieldMessages.join(", "));
    this.name = "ApiValidationError";
    this.fieldMessages = fieldMessages;
  }
}

async function throwValidationAware(response: Response): Promise<void> {
  if (response.ok || response.status === 204) return;
  const body = await response.json().catch(() => ({}));
  if (Array.isArray(body?.message)) throw new ApiValidationError(body.message);
  throwFromBody(body, response.status);
}


/** One local (flat) room row — mirrors `draftProperty.basics.rooms`; `floorId` absent means unassigned. */
export interface DraftBasicsRoomInput {
  id: string;
  name: string;
  floorId?: string;
  beds: Record<string, number>;
  size?: string;
}

/** The subset of the store's draftProperty.basics this call actually reads. */
interface DraftBasicsInput {
  bedrooms?: number;
  bathrooms?: number;
  maxGuests?: number;
  livingArea?: string;
  landSize?: string;
  /**
   * Present (either, or both) sends the FULL floor plan and replaces it —
   * confirmed live: sending `rooms` without `floors` deletes every floor.
   * Omit both to leave the floor plan untouched (a pure scalar-only save).
   */
  floors?: { id: string; name: string }[];
  rooms?: DraftBasicsRoomInput[];
  /** Only meaningful alongside `floors`/`rooms` — every floor-plan save sends the full current translation state for every row, matching the full-replace semantics above. */
  translations?: {
    rooms?: Record<string, FieldVariant[]>;
    floors?: Record<string, FieldVariant[]>;
    /** Keyed by floor-detail row id (not floor id) — a detail's id is already globally unique across floors, same as rooms above. */
    floorFeatures?: Record<string, FieldVariant[]>;
    /** Keyed by `${roomId}_${bedTypeTermId}` — a bed type's name can be overridden per room. */
    bedNames?: Record<string, FieldVariant[]>;
  };
  /** Per-floor highlight features, keyed by floor id — folded into each floor's `details[]` (FloorInputDto — BE added this field, previously client-only). */
  floorFeatures?: Record<string, { id: string; text: string; icon?: string }[]>;
}

/**
 * Builds the full floor-plan-touching save input from the store's
 * draftProperty — shared by every place that adds/edits/removes a room or
 * floor (FloorPlanSection, BedSetupModal, the bedrooms counter's "-"), since
 * the API's full-replace semantics mean each such save must resend the
 * complete tree, not just the one row that changed.
 */
export function buildBasicsFloorPlanInput(draftProperty: {
  basics: DraftBasicsInput;
  translations: { rooms?: Record<string, FieldVariant[]>; floors?: Record<string, FieldVariant[]>; floorFeatures?: Record<string, FieldVariant[]>; bedNames?: Record<string, FieldVariant[]> };
  estate?: { floorFeatures?: Record<string, { id: string; text: string; icon?: string }[]> };
}): DraftBasicsInput {
  return {
    ...draftProperty.basics,
    floors: draftProperty.basics.floors ?? [],
    rooms: draftProperty.basics.rooms ?? [],
    translations: {
      rooms: draftProperty.translations.rooms,
      floors: draftProperty.translations.floors,
      floorFeatures: draftProperty.translations.floorFeatures,
      bedNames: draftProperty.translations.bedNames,
    },
    floorFeatures: draftProperty.estate?.floorFeatures,
  };
}

/**
 * After a floor-plan-touching `patchPropertyBasics` call, maps every
 * `temp-*` floor/room id in what was *sent* to the real UUID the response
 * echoes back — by array position, since the response preserves the order
 * each floor's rooms (and the top-level unassigned list) were sent in. A
 * temp id left unreconciled would make the next save create a duplicate row
 * instead of updating the one just created.
 */
export function reconcileBasicsFloorPlan(
  sent: { floors?: { id: string; name: string }[]; rooms?: DraftBasicsRoomInput[] },
  response: NonNullable<WizardVersionPayload["basics"]>,
): { floors: { id: string; name: string }[]; rooms: DraftBasicsRoomInput[]; idMap: Record<string, string> } {
  const sentFloors = sent.floors ?? [];
  const sentRooms = sent.rooms ?? [];
  const respFloors = response.floors ?? [];
  const respRooms = response.rooms ?? [];
  const idMap: Record<string, string> = {};
  const record = (oldId: string, newId: string) => { if (newId !== oldId) idMap[oldId] = newId; };

  const floors = sentFloors.map((f, i) => {
    const realId = respFloors[i]?.id ?? f.id;
    record(f.id, realId);
    return { id: realId, name: f.name };
  });

  const rooms: DraftBasicsRoomInput[] = [];
  sentFloors.forEach((f, i) => {
    const realFloorId = respFloors[i]?.id ?? f.id;
    const respRoomsForFloor = respFloors[i]?.rooms ?? [];
    sentRooms
      .filter((r) => r.floorId === f.id)
      .forEach((r, j) => {
        const realId = respRoomsForFloor[j]?.id ?? r.id;
        record(r.id, realId);
        rooms.push({ ...r, id: realId, floorId: realFloorId });
      });
  });
  sentRooms
    .filter((r) => !r.floorId)
    .forEach((r, j) => {
      const realId = respRooms[j]?.id ?? r.id;
      record(r.id, realId);
      rooms.push({ ...r, id: realId, floorId: undefined });
    });

  return { floors, rooms, idMap };
}

/** Converts a draft room into the nested wire format accepted by the basics API. */
function roomToWire(
  room: DraftBasicsRoomInput,
  translations: FieldVariant[] | undefined,
  activeLangCodes: string[],
  bedNames: Record<string, FieldVariant[]> | undefined,
): RoomInputWire {
  // A room with no beds rolls the whole PATCH back as ROOM_WITHOUT_BED
  // (confirmed live) — callers filter these out via pruneUnconfiguredTempRooms
  // before this is ever built, but guard here too since it's a hard 400.
  const beds: BedInputWire[] = Object.entries(room.beds)
    .filter(([, count]) => count > 0)
    .map(([bedTypeTermId, count]) => ({
      bedTypeTermId,
      count,
      translations: mergeVariantsToWire({ label: bedNames?.[`${room.id}_${bedTypeTermId}`] }, activeLangCodes),
    }));
  return {
    id: UUID_RE.test(room.id) ? room.id : undefined,
    name: room.name,
    size: room.size ? parseInt(room.size, 10) : null,
    beds,
    translations: mergeVariantsToWire({ name: translations }, activeLangCodes),
  };
}

/**
 * PATCH /basics — also carries the full floor plan (floors/rooms/beds)
 * embedded in the same request now; the old separate POST/PATCH/DELETE
 * .../rooms and .../floors endpoints (and their /reorder routes) were
 * removed by the API and now 404. `basics.floors`/`basics.rooms` are only
 * built and sent when the caller passes them — a plain scalar save (Next,
 * bedrooms/bathrooms/etc.) omits both entirely so it can't accidentally wipe
 * an unrelated floor plan; a floor/room mutation always sends the complete
 * current tree (full-replace, not a delta).
 */
export async function patchPropertyBasics(
  vid: string,
  basics: DraftBasicsInput | undefined,
  options?: { skipInvalidate?: boolean },
): Promise<NonNullable<WizardVersionPayload["basics"]> & { versionId: string }> {
  const payload: UpdateBasicsBody = {
    bedrooms: Number(basics?.bedrooms) || 0,
    bathrooms: Number(basics?.bathrooms) || 0,
    maxGuests: Number(basics?.maxGuests) || 0,
    livingAreaSqft: basics?.livingArea ? Number(basics.livingArea) : 0,
    landSizeSqft: basics?.landSize ? Number(basics.landSize) : 0,
  };

  if (basics?.floors !== undefined || basics?.rooms !== undefined) {
    await ensureActiveLanguagesLoaded();
    const activeLangCodes = getActiveLanguageCodes();
    const sendableRooms = (basics.rooms ?? []).filter((r) => Object.values(r.beds).some((c) => c > 0));

    payload.floors = (basics.floors ?? []).map((f) => ({
      id: UUID_RE.test(f.id) ? f.id : undefined,
      name: f.name,
      details: (basics.floorFeatures?.[f.id] ?? []).map((d) => ({
        id: UUID_RE.test(d.id) ? d.id : undefined,
        name: d.text,
        iconKey: d.icon || null,
        translations: mergeVariantsToWire({ name: basics.translations?.floorFeatures?.[d.id] }, activeLangCodes),
      })),
      rooms: sendableRooms
        .filter((r) => r.floorId === f.id)
        .map((r) => roomToWire(r, basics.translations?.rooms?.[r.id], activeLangCodes, basics.translations?.bedNames)),
      translations: mergeVariantsToWire({ name: basics.translations?.floors?.[f.id] }, activeLangCodes),
    }));
    payload.rooms = sendableRooms
      .filter((r) => !r.floorId)
      .map((r) => roomToWire(r, basics.translations?.rooms?.[r.id], activeLangCodes, basics.translations?.bedNames));
  }

  const response = await fetch(ROUTES.WIZARD.BASICS(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  await throwIfNotOk(response);

  // Callers mid-sequence with more than one save in flight skip this —
  // invalidating here would fire WizardHydrator's refetch against a
  // mid-flight, inconsistent snapshot, landing after the sequence's own
  // final updateBasics and overwriting it with stale data. Those callers
  // invalidate once, after the whole sequence actually completes.
  if (!options?.skipInvalidate) invalidateWizardHydration();
  return response.json();
}

/** The subset of the store's draftProperty.estate this call actually reads. */
interface DraftEstateInput {
  interiorRooms?: string;
  exteriorFeatures?: string;
  /** `id` present once the row is saved — send it back to keep the row (and its translations) alive. */
  customFields?: { id?: string; heading: string; description?: string }[];
  /**
   * Per-floor highlights — accepted here so callers can keep passing the
   * whole estate object, but deliberately left out of `payload` below and
   * off `UpdateEstateBody`: confirmed against the live API docs that
   * CreateFloorDto/UpdateFloorDto/FloorResponse have no field for this yet.
   * Once BE adds one, wire it into `payload` here.
   */
  floorFeatures?: Record<string, { id: string; text: string; icon?: string }[]>;
  translations?: {
    interiorRooms?: FieldVariant[];
    exteriorFeatures?: FieldVariant[];
    /** Keyed by custom field id — the description. */
    customFields?: Record<string, FieldVariant[]>;
    /** Keyed by custom field id — the heading, sent alongside `content` in the same per-row `translations` object. */
    customFieldLabels?: Record<string, FieldVariant[]>;
  };
}

/** A custom field with no heading isn't sent at all — shared with mapCustomFieldIds so both agree on which rows the response's array positions correspond to. */
function isSendableCustomField(f: { heading: string }): boolean {
  return f.heading.trim() !== "";
}

/** Maps each submitted custom field's local temp id to the server's real one, by array position — {} when nothing changed or the counts don't line up (can't safely reconcile then). */
export function mapCustomFieldIds(
  localFields: { id?: string; heading: string }[],
  savedFields: NonNullable<WizardVersionPayload["estate"]>["customFields"],
): Record<string, string> {
  const sendable = localFields.filter(isSendableCustomField);
  const saved = savedFields ?? [];
  if (sendable.length !== saved.length) return {};
  const idMap: Record<string, string> = {};
  sendable.forEach((f, i) => {
    const realId = saved[i]?.id;
    if (f.id && realId && f.id !== realId) idMap[f.id] = realId;
  });
  return idMap;
}

export async function patchPropertyEstate(vid: string, estate: DraftEstateInput | undefined): Promise<NonNullable<WizardVersionPayload["estate"]>> {
  await ensureActiveLanguagesLoaded();
  const activeLangCodes = getActiveLanguageCodes();
  const customFields: CustomFieldInput[] = (estate?.customFields ?? [])
    .filter(isSendableCustomField)
    .map((c) => {
      const savedId = c.id && UUID_RE.test(c.id) ? c.id : undefined;
      return {
        id: savedId,
        label: c.heading,
        content: c.description ?? "",
        // Keyed by the row's own local id (temp or real) — savedId is undefined
        // until the server assigns one, which silently dropped a new field's
        // translations if looked up by savedId instead.
        translations: mergeVariantsToWire({
          content: c.id ? estate?.translations?.customFields?.[c.id] : undefined,
          label: c.id ? estate?.translations?.customFieldLabels?.[c.id] : undefined,
        }, activeLangCodes),
      };
    });

  const payload: UpdateEstateBody = {
    interiorRooms: estate?.interiorRooms ?? "",
    exteriorFeatures: estate?.exteriorFeatures ?? "",
    customFields,
    translations: mergeVariantsToWire({
      interiorRooms: estate?.translations?.interiorRooms,
      exteriorFeatures: estate?.translations?.exteriorFeatures,
    }, activeLangCodes),
  };

  const response = await fetch(ROUTES.WIZARD.ESTATE(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

/**
 * PATCH /accessibility, /safety, /policies, /important-information — split
 * out of the old combined /house-rules + /cancellation endpoints (both now
 * 404). Each caller (its own step's hook) builds the exact body shape and
 * owns its own dirty-check; these just make the call and invalidate hydration.
 */
export async function patchPropertyAccessibility(vid: string, body: UpdateAccessibilityBody): Promise<AccessibilitySection> {
  const response = await fetch(ROUTES.WIZARD.ACCESSIBILITY(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

export async function patchPropertySafety(vid: string, body: UpdateSafetyBody): Promise<SafetySection> {
  const response = await fetch(ROUTES.WIZARD.SAFETY(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

export async function patchPropertyPolicies(vid: string, body: UpdatePoliciesBody): Promise<PoliciesSection> {
  const response = await fetch(ROUTES.WIZARD.POLICIES(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

export async function patchPropertyImportantInformation(vid: string, body: UpdateImportantInformationBody): Promise<ImportantInformationSection> {
  const response = await fetch(ROUTES.WIZARD.IMPORTANT_INFORMATION(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

/** The subset of the store's draftProperty.supplier this call actually reads. */
interface DraftSupplierInput {
  name?: string;
  contactEmail?: string;
  contactPhone?: string;
  rating?: number;
  notes?: string;
  /** Only name/notes are ever translated — contactEmail/contactPhone are identifiers, not content. */
  translations?: { name?: FieldVariant[]; notes?: FieldVariant[] };
}

export async function patchPropertySupplier(vid: string, supplier: DraftSupplierInput | undefined): Promise<NonNullable<WizardVersionPayload["supplier"]>> {
  await ensureActiveLanguagesLoaded();
  const payload: UpdateSupplierBody = {
    name: supplier?.name ?? "",
    contactEmail: supplier?.contactEmail ?? "",
    contactPhone: supplier?.contactPhone ?? "",
    rating: supplier?.rating ?? null,
    notes: supplier?.notes ?? "",
    translations: mergeVariantsToWire({ name: supplier?.translations?.name, notes: supplier?.translations?.notes }, getActiveLanguageCodes()),
  };

  const response = await fetch(ROUTES.WIZARD.SUPPLIER(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

export async function patchPropertyRating(vid: string, westayRating: number | null): Promise<{ westayRating: number | null }> {
  const payload: UpdateRatingBody = { westayRating };
  const response = await fetch(ROUTES.WIZARD.RATING(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

/** The subset of the store's draftProperty this call actually reads. */
interface DraftAboutInput {
  name?: string;
  tagline?: string;
  description?: string;
  subDescription?: string;
  /** `id` is the local UI slot, unused here; `serverId` is the real row id sent back to keep the row (and its translations) alive across saves. */
  features?: { id?: number; serverId?: string; icon?: string; title?: string; desc?: string }[];
  translations?: {
    name?: FieldVariant[];
    tagline?: FieldVariant[];
    description?: FieldVariant[];
    subDescription?: FieldVariant[];
    /** Keyed by feature slot id (`String(feature.id)`) — each feature translates both its title and subtext. */
    features?: Record<string, { title?: FieldVariant[]; subtext?: FieldVariant[] }>;
  };
}

export async function patchPropertyAbout(vid: string, draft: DraftAboutInput): Promise<NonNullable<WizardVersionPayload["about"]>> {
  await ensureActiveLanguagesLoaded();
  const activeLangCodes = getActiveLanguageCodes();
  // Only title is required by FeatureCardDto — a card without one would 400.
  const features: FeatureCardInput[] = (draft.features ?? [])
    .filter((f) => f.title && f.title.trim() !== "")
    .map((f) => ({
      id: f.serverId,
      title: f.title!,
      subtext: f.desc,
      iconKey: f.icon,
      translations: mergeVariantsToWire({
        title: draft.translations?.features?.[String(f.id)]?.title,
        subtext: draft.translations?.features?.[String(f.id)]?.subtext,
      }, activeLangCodes),
    }));

  const payload: UpdateAboutBody = {
    name: draft.name || null,
    tagline: draft.tagline || null,
    description: draft.description || null,
    subDescription: draft.subDescription || null,
    features,
    translations: mergeVariantsToWire({
      name: draft.translations?.name,
      tagline: draft.translations?.tagline,
      description: draft.translations?.description,
      subDescription: draft.translations?.subDescription,
    }, activeLangCodes),
  };

  const response = await fetch(ROUTES.WIZARD.ABOUT(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

/** The subset of the store's draftProperty.location this call actually reads. */
interface DraftLocationInput {
  addressLine?: string;
  floor?: string;
  unit?: string;
  building?: string;
  city?: string;
  country?: string;
  region?: string;
  countryCode?: string;
  postalCode?: string;
  lat?: string;
  lng?: string;
  placeId?: string | Record<string, unknown> | null;
  formattedAddress?: string;
  scenicViews?: string[];
  locationFeatures?: { id: string; isSelected?: boolean }[];
  /** `id` present once the row is saved — send it back to keep the row (and its translations) alive. */
  nearbyPlaces?: { id?: string; placeType?: string; distance?: string; icon?: string; details?: string; lat?: string; lng?: string }[];
  /** Only free-text content fields — countryCode/postalCode/lat/lng/placeId aren't content. Confirmed live: a 400 explicitly names the translatable set and excludes postalCode. */
  translations?: {
    addressLine?: FieldVariant[];
    floor?: FieldVariant[];
    unit?: FieldVariant[];
    building?: FieldVariant[];
    city?: FieldVariant[];
    country?: FieldVariant[];
    region?: FieldVariant[];
    /** Keyed by nearby-place id, for its `placeType`/`distance`/`details` fields — all three translate with the place. */
    nearbyPlaces?: Record<string, { placeType?: FieldVariant[]; distance?: FieldVariant[]; details?: FieldVariant[] }>;
    /** Keyed by the same `val` string used in `scenicViews`. */
    scenicViews?: Record<string, FieldVariant[]>;
    /** Keyed by the feature's termId, same as scenicViews above. */
    locationFeatures?: Record<string, FieldVariant[]>;
  };
}

export async function patchPropertyLocation(vid: string, location: DraftLocationInput | undefined): Promise<NonNullable<WizardVersionPayload["location"]>> {
  await ensureActiveLanguagesLoaded();
  const activeLangCodes = getActiveLanguageCodes();
  const scenicViews: ScenicViewInput[] = (location?.scenicViews ?? []).map((val) => {
    const translations = mergeVariantsToWire(
      { label: location?.translations?.scenicViews?.[val] },
      activeLangCodes,
    );
    return UUID_RE.test(val)
      ? { scenicViewId: val, translations }
      : { label: val, translations };
  });

  const locationFeatures: LocationFeatureInput[] = (location?.locationFeatures ?? []).map((f) => ({
    termId: f.id,
    value: f.isSelected ?? true,
    translations: mergeVariantsToWire(
      { label: location?.translations?.locationFeatures?.[f.id] },
      activeLangCodes,
    ),
  }));

  const nearbyPlaces: NearbyPlaceInput[] = (location?.nearbyPlaces ?? []).map((p) => {
    const savedId = p.id && UUID_RE.test(p.id) ? p.id : undefined;
    return {
      id: savedId,
      placeType: p.placeType || "",
      distance: p.distance || "",
      iconKey: p.icon || "",
      details: p.details || "",
      lat: p.lat ? Number(p.lat) : null,
      lng: p.lng ? Number(p.lng) : null,
      // Keyed by the local id (p.id), not savedId — a new place's local id is
      // a temp `place_<uuid>`, so looking up by savedId (undefined pre-save) dropped its translations.
      translations: mergeVariantsToWire({
        placeType: p.id ? location?.translations?.nearbyPlaces?.[p.id]?.placeType : undefined,
        distance: p.id ? location?.translations?.nearbyPlaces?.[p.id]?.distance : undefined,
        details: p.id ? location?.translations?.nearbyPlaces?.[p.id]?.details : undefined,
      }, activeLangCodes),
    };
  });

  const payload: UpdateLocationBody = {
    addressLine: location?.addressLine || "",
    floor: location?.floor || "",
    aptUnit: location?.unit || "",
    building: location?.building || "",
    city: location?.city || "",
    country: location?.country || "",
    region: location?.region || "",
    countryCode: location?.countryCode || "",
    postalCode: location?.postalCode || "",
    formattedAddress: location?.formattedAddress || "",
    lat: location?.lat ? Number(location.lat) : null,
    lng: location?.lng ? Number(location.lng) : null,
    scenicViews,
    locationFeatures,
    nearbyPlaces,
    translations: mergeVariantsToWire({
      addressLine: location?.translations?.addressLine,
      floor: location?.translations?.floor,
      aptUnit: location?.translations?.unit,
      building: location?.translations?.building,
      city: location?.translations?.city,
      country: location?.translations?.country,
      region: location?.translations?.region,
    }, activeLangCodes),
  };

  if (typeof location?.placeId === "string" && location.placeId.trim() !== "") {
    payload.placeId = location.placeId;
  }

  const response = await fetch(ROUTES.WIZARD.LOCATION(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  await throwValidationAware(response);
  invalidateWizardHydration();
  return response.json();
}

/**
 * GET /property-versions/{vid} — the full wizard payload (about, location,
 * basics, estate, amenities, photos, etc.) in one call. Used to hydrate
 * `wizard-store` with real server state when opening/switching to a property
 * version, instead of trusting whatever was last persisted locally for a
 * possibly different version.
 */
export async function getWizardPayload(vid: string, signal?: AbortSignal, section?: string): Promise<WizardVersionPayload> {
  const query = section ? `?section=${section}` : "";
  const response = await fetch(`${ROUTES.WIZARD.PROPERTY(vid)}${query}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    // A save right beforehand invalidates the caller's own "already fetched"
    // flag specifically so the next call here gets fresh step-completion
    // data — the browser's own HTTP cache serving a stale response for this
    // exact URL would silently defeat that.
    cache: "no-store",
    signal,
  });
  await throwIfNotOk(response);
  return response.json();
}

/** DELETE /property-versions/{vid} — discards a draft (and the property with it, if this was its only version). 409 if the version isn't a draft. */
export async function discardDraft(vid: string): Promise<void> {
  const response = await fetch(ROUTES.WIZARD.PROPERTY(vid), { method: "DELETE" });
  await throwIfNotOk(response);
}

/** Published is a further, read-only state of Approved — every "Approved" figure in the UI folds Published into it (see loadProperties in catalogue-store.ts). */
export function foldPublishedIntoApproved(approved: number, published: number): number {
  return approved + published;
}

/** GET /properties/counts — counts behind the dashboard's tab badges, across every status. */
export async function getPropertyTabCounts(
  signal?: AbortSignal,
  type?: PropertyType,
): Promise<PropertyTabCounts> {
  const query = type ? `?type=${encodeURIComponent(type)}` : "";
  const response = await fetch(`${ROUTES.WIZARD.COUNTS}${query}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    // A status change (approve/reject/publish) right beforehand must be
    // reflected immediately — a cached response for this URL would show
    // stale counts.
    cache: "no-store",
    signal,
  });
  // Counts cover every status; roles without view-all 403. Callers skip this
  // when they can, but a leftover 403 must not block login with the modal.
  await throwIfNotOk(response, { silent403: true });
  return response.json();
}

const PROPERTY_TABS = ["draft", "pending", "approved", "rejected", "published"] as const;
const PROPERTY_LIST_PAGE_LIMIT = 100; // the API's own max

/**
 * Per-type counts across every tab. No "count by type" endpoint exists, so
 * this tallies the `type` on each row from /properties?tab=X (one call per
 * tab, not one per type×tab) instead of querying each type separately.
 * allSettled since `tab` is role-gated (403 for a tab you can't see) — one
 * failed tab shouldn't zero the rest.
 */
export async function getPropertyTypeCounts(
  signal?: AbortSignal,
  tabs: readonly (typeof PROPERTY_TABS)[number][] = PROPERTY_TABS,
): Promise<Partial<Record<PropertyType, number>>> {
  const counts: Partial<Record<PropertyType, number>> = {};

  await Promise.allSettled(
    tabs.map(async (tab) => {
      let page = 1;
      let totalPages = 1;
      do {
        const params = new URLSearchParams({ tab, page: String(page), limit: String(PROPERTY_LIST_PAGE_LIMIT) });
        const response = await fetch(`${ROUTES.WIZARD.PROPERTIES}?${params.toString()}`, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
          signal,
        });
        // Role-gated tabs 403; don't pop Access Restricted for a probe we already expect to fail.
        await throwIfNotOk(response, { silent403: true });
        const data: { items?: { type?: string }[]; totalPages?: number } = await response.json();
        (data.items ?? []).forEach((item) => {
          if (!item.type) return;
          counts[item.type as PropertyType] = (counts[item.type as PropertyType] ?? 0) + 1;
        });
        totalPages = data.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages);
    }),
  );

  return counts;
}

/** One cheap `total` per tab the role can actually list — used when /counts is 403. */
export async function getAccessibleTabCounts(
  tabs: readonly PropertyListTab[],
  signal?: AbortSignal,
  type?: PropertyType,
): Promise<Partial<Record<PropertyListTab, number>>> {
  const counts: Partial<Record<PropertyListTab, number>> = {};

  async function fetchTabTotal(tab: PropertyListTab): Promise<number> {
    const params = new URLSearchParams({ tab, page: "1", limit: "1" });
    // Drafts are own-scope and not category-filtered on the list.
    if (type && tab !== "draft") params.set("type", type);
    const response = await fetch(`${ROUTES.WIZARD.PROPERTIES}?${params.toString()}`, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    });
    await throwIfNotOk(response, { silent403: true });
    const data: { total?: number } = await response.json();
    return data.total ?? 0;
  }

  // One tab at a time — five parallel /properties calls plus the list
  // fetch 502'd the catalogue gateway on type-scoped pages.
  for (const tab of tabs) {
    // Must reject on abort, not resolve with a partial map — the caller has no staleness check of its own.
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      counts[tab] = await fetchTabTotal(tab);
    } catch (err) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      if (err instanceof AccessRestrictedError) continue;
      throw err;
    }
  }

  // The Approved chip also counts published properties (see loadProperties
  // in catalogue-store.ts) — reuse the published total already fetched above
  // when it's in the same tab set, otherwise fetch it just for this sum.
  if (tabs.includes("approved") && counts.approved != null) {
    try {
      const publishedTotal = counts.published ?? (await fetchTabTotal("published"));
      counts.approved = foldPublishedIntoApproved(counts.approved, publishedTotal);
    } catch (err) {
      if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      if (!(err instanceof AccessRestrictedError)) throw err;
    }
  }

  return counts;
}

/** Resolve the listed version for a property so preview/edit can open with `?vid=`. */
export async function findListedPropertyVersion(
  propertyId: string,
  signal?: AbortSignal,
): Promise<{ propertyId: string; versionId: string } | null> {
  for (const tab of ["published", "approved", "pending", "draft", "rejected"] as const) {
    try {
      let page = 1;
      let totalPages = 1;
      do {
        const params = new URLSearchParams({
          tab,
          page: String(page),
          limit: String(PROPERTY_LIST_PAGE_LIMIT),
        });
        const response = await fetch(`${ROUTES.WIZARD.PROPERTIES}?${params.toString()}`, {
          method: "GET",
          headers: { Accept: "application/json" },
          signal,
        });
        await throwIfNotOk(response, { silent403: true });
        const data: {
          items?: { propertyId?: string; versionId?: string }[];
          totalPages?: number;
        } = await response.json();
        const hit = (data.items ?? []).find((item) => item.propertyId === propertyId && item.versionId);
        if (hit?.versionId) {
          return { propertyId: hit.propertyId!, versionId: hit.versionId };
        }
        totalPages = data.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages);
    } catch (err) {
      if (err instanceof AccessRestrictedError) continue;
      throw err;
    }
  }
  return null;
}

// ── Taxonomy ──────────────────────────────────────────────────────────────────

/**
 * GET /taxonomy/terms?groups=media_categories
 * Returns the curated list of photo category options (Pool & Garden, Exterior, etc.).
 * Safe to fetch once per session and cache — changes only when the seed is re-run.
 */
export async function getTaxonomyTerms(groups: string, locale?: string, signal?: AbortSignal): Promise<TaxonomyGroup[]> {
  const params = new URLSearchParams({ groups });
  // The API's own docs: "Omit for English" — it treats an explicit `en` as an
  // unrecognized locale code and 400s, rather than as "the default".
  if (locale && locale !== "en") params.set("locale", locale);
  const response = await fetch(`${ROUTES.TAXONOMY.TERMS}?${params.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  await throwIfNotOk(response);
  return response.json();
}

/** GET /taxonomy/locales — safe to fetch once per session and cache, like getTaxonomyTerms above. */
export async function getContentLocales(signal?: AbortSignal): Promise<ContentLocale[]> {
  const response = await fetch(ROUTES.TAXONOMY.LOCALES, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  await throwIfNotOk(response);
  return response.json();
}

// ── Step 7 — Media (Photos & Videos) ──────────────────────────────────────────

// ── Photos arrangement (PATCH /photos) ───────────────────────────────────────

/**
 * A single scope group sent to PATCH /photos.
 * `scopeId` is the categoryId (property), roomId (room), or amenityCategoryId (amenity).
 * `mediaIds` is the ordered array of mediaIds within that group (max 200).
 */
export interface PhotosArrangementGroup {
  scope: "property" | "room" | "amenity" | "floor" | "video";
  scopeId: string;
  mediaIds: string[];
}

export interface PhotosArrangementBody {
  /** Ordered mediaIds to set as the hero carousel (max 5). Omit to leave heroes unchanged. */
  heroes?: string[];
  /** In-group sort order for each scope bucket. Omit to leave order unchanged. */
  groups?: PhotosArrangementGroup[];
}

/**
 * PATCH /photos — batched autosave for photo arrangement.
 * Saves hero order and within-folder sort order in a single call.
 * Returns the full updated MediaListResponse.
 */
export async function patchPropertyPhotos(
  vid: string,
  body: PhotosArrangementBody,
): Promise<MediaListResponse> {
  const response = await fetch(ROUTES.WIZARD.PHOTOS(vid), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response);
  invalidateWizardHydration();
  return response.json();
}

/**
 * GET /property-versions/{vid}?section=photos — the dedicated GET /media endpoint
 * was removed in favor of the unified wizard-payload endpoint; this section
 * returns { photos: { heroes, property, rooms, amenities, videos } }, unwrapped here.
 */
export async function getPropertyMedia(vid: string, signal?: AbortSignal): Promise<MediaListResponse> {
  const response = await fetch(ROUTES.WIZARD.MEDIA(vid), {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  await throwIfNotOk(response);
  const body = await response.json() as { photos: MediaListResponse };
  return body.photos;
}

/**
 * POST /media/upload — uploads photos to the `property` bucket (multipart).
 *
 * The API stores photos in flat buckets — no categoryId for property-level
 * uploads. Pass roomId or amenityId only when targeting those buckets.
 *
 * The correct form field name must match what the API expects.
 * Current confirmed field name: determined by testing against live API.
 *
 * Validates each file against the API's own documented contract (jpeg/png/webp/gif,
 * max 20MB each) before ever sending the request — fast client-side feedback only;
 * the server is still the source of truth and would 400 on the same files anyway.
 * Rejects the whole batch on any invalid file rather than silently dropping some,
 * so the caller's existing optimistic-preview rollback covers this the same way
 * it already handles a real upload failure — no per-caller changes needed.
 *
 * `options.scope: "host"` uploads a host profile photo instead (wizard step 10),
 * mutually exclusive with categoryId/roomId/amenityCategoryId. Host photos
 * never appear in the property/room/amenity media list.
 *
 * `options.categoryName` finds or creates a `media_categories` term instead of
 * using `categoryId` — mutually exclusive with it.
 */
export async function uploadPropertyMedia(
  vid: string,
  files: File[],
  options?: { categoryId?: string; categoryName?: string; roomId?: string; amenityCategoryId?: string; floorId?: string; scope?: "host" },
): Promise<MediaUploadResult[]> {
  const acceptedTypes = ACCEPTED_PHOTO_TYPES.split(",");
  const invalid = files.filter(
    (f) => !acceptedTypes.includes(f.type) || f.size > PHOTO_MAX_BYTES,
  );
  if (invalid.length > 0) {
    const names = invalid.map((f) => f.name).join(", ");
    throw new Error(
      `${invalid.length > 1 ? "These files aren't" : "This file isn't"} supported: ${names}. ` +
      `Use JPEG, PNG, WebP, or GIF, up to 20MB each.`,
    );
  }

  const form = new FormData();
  // "files" (plural, required), plus exactly one of: categoryId (property folder), roomId, amenityCategoryId, floorId, or scope=host
  files.forEach((file) => form.append("files", file));
  if (options?.categoryId) form.append("categoryId", options.categoryId);
  if (options?.categoryName) form.append("categoryName", options.categoryName);
  if (options?.roomId) form.append("roomId", options.roomId);
  if (options?.amenityCategoryId) form.append("amenityCategoryId", options.amenityCategoryId);
  if (options?.floorId) form.append("floorId", options.floorId);
  if (options?.scope) form.append("scope", options.scope);

  const response = await fetch(ROUTES.WIZARD.MEDIA_UPLOAD(vid), {
    method: "POST",
    body: form,
    // Do NOT set Content-Type — fetch sets it with the correct multipart boundary
  });
  await throwIfNotOk(response);
  const data = await response.json();
  // API may return a single object or an array — normalise to array
  return Array.isArray(data) ? data : [data];
}

/** DELETE /media/categories/{categoryId} */
export async function deletePropertyMediaCategory(
  vid: string,
  categoryId: string,
): Promise<void> {
  const response = await fetch(
    ROUTES.WIZARD.MEDIA_CATEGORY(vid, categoryId),
    { method: "DELETE" },
  );
  await throwIfNotOk(response);
}

/** DELETE /media/{mediaId} */
export async function deletePropertyMediaItem(
  vid: string,
  mediaId: string,
): Promise<void> {
  const response = await fetch(ROUTES.WIZARD.MEDIA_ITEM(vid, mediaId), {
    method: "DELETE",
  });
  await throwIfNotOk(response);
}

// ── Video upload sessions (resumable) ─────────────────────────────────────────

export async function startVideoUploadSession(
  vid: string,
  payload: { fileName: string; contentType: string; size: number },
): Promise<VideoUploadSession> {
  const response = await fetch(
    ROUTES.WIZARD.VIDEO_UPLOAD_SESSIONS(vid),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  await throwIfNotOk(response);
  return response.json();
}

export async function getVideoUploadSession(
  vid: string,
  sessionId: string,
): Promise<VideoUploadSessionStatus> {
  const response = await fetch(
    ROUTES.WIZARD.VIDEO_UPLOAD_SESSION(vid, sessionId),
    { method: "GET", headers: { Accept: "application/json" } },
  );
  await throwIfNotOk(response);
  return response.json();
}

export async function completeVideoUploadSession(
  vid: string,
  sessionId: string,
): Promise<VideoUploadComplete> {
  const response = await fetch(
    ROUTES.WIZARD.VIDEO_UPLOAD_SESSION_COMPLETE(vid, sessionId),
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
  );
  await throwIfNotOk(response);
  return response.json();
}
